package er.prateek.crossly.ads

import er.prateek.crossly.ads.entitlement.Entitlement
import er.prateek.crossly.ads.entitlement.EntitlementProvider
import er.prateek.crossly.config.AppConfig
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.firebase.AnalyticsLogger
import er.prateek.crossly.firebase.RemoteConfigClient
import er.prateek.crossly.firebase.RemoteConfigKeys
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * The one thing the rest of the app talks to about ads.
 *
 * Screens ask [isBannerAllowed]; the results flow calls [onPuzzleCompleted]. Neither knows
 * about AdMob, AdSense, Remote Config, or entitlements, which is what lets the whole stack
 * be re-pointed -- to a different network, or to "off, this user paid" -- without touching a
 * single screen.
 */
class AdsCoordinator(
    private val remoteConfig: RemoteConfigClient,
    private val entitlementProvider: EntitlementProvider,
    private val frequency: AdFrequencyController,
    private val host: AdHost,
    private val analytics: AnalyticsLogger,
    /**
     * Reads the user's analytics opt-out. Declining analytics implies declining ad
     * personalization -- offering "don't track me" and then serving profiled ads would make
     * the setting a lie, so the two are deliberately coupled here rather than split into a
     * second consent switch.
     */
    private val personalizationAllowed: () -> Boolean = { true },
) {
    private val configState = MutableStateFlow(AdsConfig.DEFAULT)

    /** Compose reads this, so a Remote Config fetch mid-session re-lays-out banner slots. */
    val config: StateFlow<AdsConfig> = configState.asStateFlow()

    val entitlement: StateFlow<Entitlement> = entitlementProvider.entitlement

    /**
     * Call once at app start, after Remote Config has been warmed up. Safe to call again.
     */
    suspend fun start() {
        entitlementProvider.refresh()
        reloadConfig()

        val current = configState.value
        if (!AdsPolicy.adsAllowed(current, entitlement.value)) return
        // Initializing the SDK is itself a tracked event on most networks, so it is done
        // only once we know this user is going to be shown something.
        host.initialize(current)
        if (AdsPolicy.interstitialAllowed(current, entitlement.value)) host.preloadInterstitial(current)
        if (AdsPolicy.rewardedAllowed(current, entitlement.value)) host.preloadRewarded(current)
    }

    /** Re-reads `app_config`'s `ads` section from whatever Remote Config has activated. */
    fun reloadConfig() {
        val parsed = AppConfig.parse(remoteConfig.getString(RemoteConfigKeys.APP_CONFIG)).ads
        configState.value = parsed.copy(
            nonPersonalized = parsed.nonPersonalized || !personalizationAllowed(),
        )
    }

    fun isBannerAllowed(placement: AdPlacement): Boolean =
        AdsPolicy.bannerAllowed(configState.value, entitlement.value, placement)

    /**
     * The interstitial decision point. Returns whether an ad was shown, though callers
     * mostly ignore it -- the result screen renders the same either way.
     *
     * Counting the puzzle happens even when ads are off entirely, so that turning ads on via
     * Remote Config doesn't immediately fire one at a player whose counter never moved.
     */
    suspend fun onPuzzleCompleted(trigger: AdTrigger): Boolean {
        frequency.recordPuzzleCompleted()

        val current = configState.value
        if (!host.supportsFullScreenAds) return false
        if (!AdsPolicy.interstitialAllowed(current, entitlement.value)) return false
        if (!frequency.shouldShowInterstitial(current.interstitial)) return false

        val shown = host.showInterstitial(current)
        if (shown) {
            frequency.recordInterstitialShown()
            analytics.logEvent(
                AnalyticsEvents.AD_INTERSTITIAL_SHOWN,
                mapOf(
                    "trigger" to trigger.wireName,
                    "non_personalized" to current.nonPersonalized,
                    "every_n_puzzles" to current.interstitial.everyNPuzzles,
                    "max_per_session" to current.interstitial.maxPerSession,
                ),
            )
        } else {
            // Almost always "no fill / not loaded yet" rather than an error worth surfacing.
            analytics.logEvent(
                AnalyticsEvents.AD_SHOW_FAILED,
                mapOf("format" to "interstitial", "trigger" to trigger.wireName),
            )
        }
        // Start fetching the next one now; it has until the player's next solve to arrive.
        host.preloadInterstitial(current)
        return shown
    }

    /**
     * Offers a rewarded ad. Returns `true` only if the reward was genuinely earned -- callers
     * must not grant anything on `false`.
     */
    suspend fun showRewarded(placement: AdPlacement): Boolean {
        val current = configState.value
        if (!host.supportsFullScreenAds) return false
        if (!AdsPolicy.rewardedAllowed(current, entitlement.value)) return false
        if (!frequency.canShowRewarded(current.rewarded)) return false

        val earned = host.showRewarded(current)
        if (earned) {
            frequency.recordRewardedShown()
            analytics.logEvent(
                AnalyticsEvents.AD_REWARDED_EARNED,
                mapOf(
                    "placement" to placement.wireName,
                    "max_per_day" to current.rewarded.maxPerDay,
                    "non_personalized" to current.nonPersonalized,
                ),
            )
        } else {
            analytics.logEvent(
                AnalyticsEvents.AD_SHOW_FAILED,
                mapOf("format" to "rewarded", "placement" to placement.wireName),
            )
        }
        host.preloadRewarded(current)
        return earned
    }

    /** Whether a rewarded ad is worth offering at all -- drives whether to show the button. */
    fun isRewardedAvailable(): Boolean {
        val current = configState.value
        return host.supportsFullScreenAds &&
            AdsPolicy.rewardedAllowed(current, entitlement.value) &&
            frequency.canShowRewarded(current.rewarded)
    }

    fun logBannerShown(placement: AdPlacement) {
        analytics.logEvent(
            AnalyticsEvents.AD_BANNER_SHOWN,
            mapOf("placement" to placement.wireName, "non_personalized" to configState.value.nonPersonalized),
        )
    }
}
